import * as React from "react";
import { Button, Card, CardHeader, List, ListItemButton, ListItemText, Menu, MenuItem } from "@mui/material";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react";

import { dayjs } from "@/lib/dayjs";
import type { LeadSearchSummary } from "@/hooks/api/use-lead-finder";

const detail = ({ total, updatedAt }: LeadSearchSummary) =>
	[total === null ? "No search yet" : `${total.toLocaleString()} contacts`, dayjs(updatedAt).fromNow()].join(" · ");

export interface RecentSearchesProps {
	searches: LeadSearchSummary[];
	onOpen: (id: string) => void;
}

/** Recent searches on the empty page, to carry on with one. */
export function RecentSearchesList({ searches, onOpen }: RecentSearchesProps): React.JSX.Element | null {
	if (searches.length === 0) return null;
	return (
		<Card sx={{ maxWidth: 760, width: "100%", mx: "auto" }}>
			<CardHeader title="Recent searches" titleTypographyProps={{ variant: "h6" }} />
			<List disablePadding>
				{searches.slice(0, 8).map((search) => (
					<ListItemButton key={search.id} divider onClick={() => onOpen(search.id)}>
						<ListItemText primary={search.title} secondary={detail(search)} />
					</ListItemButton>
				))}
			</List>
		</Card>
	);
}

/** Recent searches from inside a conversation. */
export function RecentSearchesMenu({ searches, onOpen }: RecentSearchesProps): React.JSX.Element | null {
	const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
	if (searches.length === 0) return null;
	return (
		<>
			<Button
				variant="outlined"
				startIcon={<ClockCounterClockwiseIcon />}
				onClick={(event) => setAnchor(event.currentTarget)}
			>
				Recent searches
			</Button>
			<Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
				{searches.map((search) => (
					<MenuItem
						key={search.id}
						onClick={() => {
							setAnchor(null);
							onOpen(search.id);
						}}
					>
						<ListItemText primary={search.title} secondary={detail(search)} />
					</MenuItem>
				))}
			</Menu>
		</>
	);
}
